/**
 * Daily streak card (specs/home-page AC-33, AC-34): the 7-day claim ladder at a glance. Presentational:
 * the model comes from `useHomeStreak`; a tap in any state opens the shared Daily reward sheet (the one
 * behind the menu's coin chip), where Collect runs the shared claim and the coins fly to the header, so
 * this card and the Profile's Daily Rewards card can never disagree or double-claim.
 */

import React, { useEffect, useMemo, useRef } from 'react';
import { View, Text, Pressable, Platform, Animated, Easing } from 'react-native';
import { Flame } from 'lucide-react-native';
import { WEB_HOVER_TRANSITION_STYLE } from '../../../src/shared/constants/animations';
import { useTheme } from '../../../src/theme/useTheme';
import { HOME_COPY, HOME_TEST_IDS } from '../../../src/home/constants/home.constants';
import type { HomeStreakDotState, HomeStreakModel } from '../../../src/home/types/home.types';
import { STREAK_FLAME_SIZE, getStreakCardStyles } from './StreakCard.styles';

type DotStyleKey = 'dotClaimed' | 'dotToday' | 'dotLocked';
type DotLabelStyleKey = 'dotLabelClaimed' | 'dotLabelToday' | 'dotLabelLocked';

const FLAME_FLICKER_MS = 900;
const TODAY_PULSE_MS = 700;

export interface StreakCardProps {
  model: HomeStreakModel;
  /** Opens the Daily reward sheet (ladder, Collect when today is unclaimed, the countdown after). */
  onPress: () => void;
}

const DOT_STYLE: Record<HomeStreakDotState, DotStyleKey> = {
  claimed: 'dotClaimed',
  today: 'dotToday',
  locked: 'dotLocked',
};
const DOT_LABEL_STYLE: Record<HomeStreakDotState, DotLabelStyleKey> = {
  claimed: 'dotLabelClaimed',
  today: 'dotLabelToday',
  locked: 'dotLabelLocked',
};

function useFlameFlicker(): Animated.Value {
  const scale = useRef(new Animated.Value(1)).current;
  useEffect(() => {
    const loop = Animated.loop(
      Animated.sequence([
        Animated.timing(scale, { toValue: 1.15, duration: FLAME_FLICKER_MS, easing: Easing.inOut(Easing.ease), useNativeDriver: true }),
        Animated.timing(scale, { toValue: 0.94, duration: FLAME_FLICKER_MS * 0.9, easing: Easing.inOut(Easing.ease), useNativeDriver: true }),
        Animated.timing(scale, { toValue: 1, duration: FLAME_FLICKER_MS, easing: Easing.inOut(Easing.ease), useNativeDriver: true }),
      ]),
    );
    loop.start();
    return () => loop.stop();
  }, [scale]);
  return scale;
}

function useTodayPulse(active: boolean): Animated.Value {
  const opacity = useRef(new Animated.Value(1)).current;
  useEffect(() => {
    if (!active) return;
    const loop = Animated.loop(
      Animated.sequence([
        Animated.timing(opacity, { toValue: 0.35, duration: TODAY_PULSE_MS, easing: Easing.inOut(Easing.ease), useNativeDriver: true }),
        Animated.timing(opacity, { toValue: 1, duration: TODAY_PULSE_MS, easing: Easing.inOut(Easing.ease), useNativeDriver: true }),
      ]),
    );
    loop.start();
    return () => {
      loop.stop();
      opacity.setValue(1);
    };
  }, [active, opacity]);
  return opacity;
}

export function streakStatusText(model: HomeStreakModel): string | null {
  if (model.canClaim) return HOME_COPY.streakCollect(model.todayReward);
  if (model.weekComplete) return HOME_COPY.STREAK_COMPLETE;
  if (model.nextIn) return `${HOME_COPY.STREAK_NEXT_IN} ${model.nextIn}`;
  return null;
}

export const StreakCard: React.FC<StreakCardProps> = ({ model, onPress }) => {
  const theme = useTheme();
  const styles = useMemo(() => getStreakCardStyles(theme), [theme]);
  const flameScale = useFlameFlicker();
  const todayPulse = useTodayPulse(model.canClaim);
  const statusText = streakStatusText(model);
  const unit = model.days === 1 ? HOME_COPY.STREAK_DAY : HOME_COPY.STREAK_DAYS;

  return (
    <Pressable
      style={({ pressed, hovered }) => [
        styles.card,
        Platform.select({ web: WEB_HOVER_TRANSITION_STYLE }),
        pressed && styles.cardPressed,
        hovered && Platform.OS === 'web' && styles.cardHover,
      ]}
      onPress={onPress}
      accessibilityRole="button"
      accessibilityLabel={statusText ? `${HOME_COPY.STREAK_LABEL}, ${statusText}` : HOME_COPY.STREAK_LABEL}
      accessibilityHint={HOME_COPY.STREAK_OPEN_HINT}
      testID={HOME_TEST_IDS.streakCard}
    >
      <View style={styles.headerText}>
        <Text style={styles.label}>{HOME_COPY.STREAK_LABEL}</Text>
        <Text style={styles.subtitle} numberOfLines={1}>{HOME_COPY.STREAK_SUBTITLE}</Text>
      </View>
      <View style={styles.center}>
        <Animated.View style={[styles.flame, { transform: [{ scale: flameScale }] }]}>
          <Flame size={STREAK_FLAME_SIZE} color={theme.accentDark} fill={theme.accent} strokeWidth={1.6} />
        </Animated.View>
        <View style={styles.countRow}>
          <Text style={styles.count} testID={HOME_TEST_IDS.streakDays}>{model.days}</Text>
          <Text style={styles.countUnit}>{unit}</Text>
        </View>
      </View>
      <View style={styles.dots}>
        {model.dots.map((dot) => (
          <View key={dot.day} style={styles.dotColumn}>
            {dot.state === 'today' ? (
              <Animated.View
                style={[styles.dot, styles.dotToday, { opacity: todayPulse }]}
                testID={`${HOME_TEST_IDS.streakDot}-${dot.day}-today`}
              />
            ) : (
              <View style={[styles.dot, styles[DOT_STYLE[dot.state]]]} testID={`${HOME_TEST_IDS.streakDot}-${dot.day}-${dot.state}`} />
            )}
            <Text style={[styles.dotLabel, styles[DOT_LABEL_STYLE[dot.state]]]}>{HOME_COPY.streakDot(dot.day)}</Text>
          </View>
        ))}
      </View>
      {statusText && (
        <Text style={[styles.status, model.canClaim ? styles.statusCollect : styles.statusWait]} testID={HOME_TEST_IDS.streakStatus}>
          {statusText}
        </Text>
      )}
    </Pressable>
  );
};
